/**
 * <s-product-gallery> — PDP media carousel with prev/next arrows and thumbnails.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --prev   — Scroll to the previous slide (stops at the first).
 *   --next   — Scroll to the next slide (stops at the last).
 *   --select — Scroll to the slide whose index is in the source's data-index.
 *
 * Required child markup:
 *   [data-gallery-track]  — Horizontal scroll-snap track.
 *   [data-gallery-slide]  — Each slide inside the track.
 *   [data-gallery-prev]   — Previous button (disabled on the first slide).
 *   [data-gallery-next]   — Next button (disabled on the last slide).
 *   [data-gallery-thumb]  — Thumbnail buttons with data-index; the active one gets aria-current.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #handleCommand = (event) => {
    if (event.command === '--prev') this.#goTo(this.#index - 1);
    else if (event.command === '--next') this.#goTo(this.#index + 1);
    else if (event.command === '--select') this.#goTo(Number(event.source?.dataset.index));
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      const offset = Math.abs(this.#track.scrollLeft);
      const index = Math.round(offset / width);
      if (index !== this.#index) {
        this.#index = Math.min(Math.max(index, 0), this.#slides.length - 1);
        this.#update();
      }
    });
  };

  #goTo(index) {
    if (!Number.isFinite(index)) return;
    const target = this.#slides[Math.min(Math.max(index, 0), this.#slides.length - 1)];
    if (!target) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Relative to the first slide so the value is negative in RTL, matching scrollLeft there.
    const left = target.offsetLeft - this.#slides[0].offsetLeft;
    this.#track.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });

    this.#index = this.#slides.indexOf(target);
    this.#update();
  }

  // Scrolls only the thumbnail strip, never the page, to keep the active thumb visible.
  #revealThumb(thumb) {
    const strip = thumb.closest('[data-gallery-thumbs]');
    if (!strip) return;
    // The strip is position: relative, so offsetLeft is measured from its edge.
    const start = thumb.offsetLeft;
    const end = start + thumb.offsetWidth;
    if (start < strip.scrollLeft) strip.scrollLeft = start;
    else if (end > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = end - strip.clientWidth;
  }

  #update() {
    const last = this.#slides.length - 1;
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= last;

    for (const thumb of this.#thumbs) {
      const active = Number(thumb.dataset.index) === this.#index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        this.#revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
